<template>
  <div v-if="isAuthenticated">
    <YapBannerNotifications class="sticky top-14 z-50" />
    <YapRefreshToken :debug="getEnv('VITE_APP_ENV') !== 'prod'" />
    <IdleDetectHandler v-if="state.refreshTokenExpLoaded" />
    <div class="flex h-screen w-full items-center justify-center" v-show="!state.isRefdataLoaded">
      <YapLoader :size="YapLoaderSizes.Medium" class="text-yapgray-700 dark:text-yapgray-100" />
    </div>
    <YapBase>
      <template #navbar>
        <Navbar />
      </template>
      <template #side-menu>
        <SideMenu @on-navigate="state.showingNavigationDropdown = false" />
      </template>
      <template #content>
        <router-view v-if="state.isRefdataLoaded" />
      </template>
    </YapBase>
  </div>
  <div v-else>
    <router-view />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { AxiosResponse } from 'axios';
import { computed, onBeforeUnmount, onMounted, reactive, watch } from 'vue';

import getEnv from '@/utils/env';
import apiGw from '@/services/apiGw';
import { Theme } from '@/types/general';
import { useI18n } from '@/plugins/i18n';
import YapBase from '@/layouts/YapBase.vue';
import Navbar from '@/components/navigation/Navbar.vue';
import { useAuthStore } from '@/store/authStore';
import SideMenu from '@/components/SideMenu.vue';
import { useUserStore } from '@/store/userStore';
import { useProfileStore } from '@/store/profileStore';
import { useRefdataStore } from '@/store/refdataStore';
import { useExportsStore } from '@/store/exportsStore';
import YapRefreshToken from '@/components/YapRefreshToken.vue';
import { AlertTypes, useAlertStore } from '@/store/alertsStore';
import IdleDetectHandler from '@/components/IdleDetectHandler.vue';
import YapLoader, { YapLoaderSizes } from '@/components/YapLoader.vue';
import YapBannerNotifications from '@/components/notification/YapBannerNotifications.vue';
import { NaturalPersonURI } from '@/types/naturalPerson';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import { createResourceUri } from '@/utils/links';
import { PartnershipStatus } from '@/types/partnership';

interface State {
  isDarkTheme: boolean;
  showingNavigationDropdown: boolean;
  isRefdataLoaded: boolean;
  refreshTokenExpLoaded: boolean;
}

const i18n = useI18n();
const authStore = useAuthStore();
const userStore = useUserStore();
const contextSwitchStore = useContextSwitchStore();
const partnershipsStore = usePartnershipsStore();
const alertStore = useAlertStore();
const refdataStore = useRefdataStore();
const profileStore = useProfileStore();
const exportsStore = useExportsStore();

const state: State = reactive({
  isDarkTheme:
    localStorage.theme === Theme.DARK_THEME ||
    (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches),
  showingNavigationDropdown: false,
  isRefdataLoaded: false,
  refreshTokenExpLoaded: false
});

const { communication } = storeToRefs(profileStore);

const isAuthenticated = computed(() => authStore.authenticated);

// On page load or when changing themes, best to add inline in `head` to avoid FOUC
if (
  localStorage.theme === Theme.DARK_THEME ||
  (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
) {
  document.documentElement.classList.add(Theme.DARK_THEME);
  profileStore.setTheme(Theme.DARK_THEME);
} else {
  document.documentElement.classList.remove(Theme.DARK_THEME);
  profileStore.setTheme(Theme.LIGHT_THEME);
}

function updateOfflineStatus() {
  if (navigator.onLine) {
    alertStore.removeAlert('offline');
  } else {
    alertStore.addAlert({
      id: 'offline',
      title: 'app.error.offline.title',
      message: 'app.error.offline.txt',
      alertTimestamp: Date.now(),
      alertType: AlertTypes.Error
    });
  }
}

onMounted(async () => {
  window.addEventListener('online', () => updateOfflineStatus());
  window.addEventListener('offline', () => updateOfflineStatus());

  if (isAuthenticated.value) {
    if (!authStore.refreshTokenExp) {
      await authStore.refreshToken();
    }
    state.refreshTokenExpLoaded = true;
    loadData();
  }
  const favicon = document.getElementById('favicon') as HTMLLinkElement;
  if (favicon) {
    favicon.href = `/images/favicons/favicon_${getEnv('VITE_APP_ENV')}.svg`;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('online', updateOfflineStatus);
  window.removeEventListener('offline', updateOfflineStatus);
});

watch(
  () => isAuthenticated.value,
  (newVal) => {
    if (newVal) {
      loadData();
      state.refreshTokenExpLoaded = true;
    }
  }
);

async function loadData() {
  await loadUserData();
  loadRequiredRefdata();
}

async function loadRequiredRefdata() {
  const collections = [
    'dict',
    'confirmation',
    'company-declaration',
    'conditions',
    'agreements',
    'app.home',
    'app.error',
    'app.event',
    'webapp.nav',
    'app.pricing',
    'webapp.token',
    'webapp.pricing',
    'webapp.settings',
    'webapp.error',
    'webapp.cards',
    'app.payments',
    'app.settings',
    'app.partnership',
    'app.cardControl',
    'webapp.payments',
    'app.transactions',
    'webapp.dashboard',
    'webapp.accountInfo',
    'webapp.transactions',
    'transaction-categories',
    'transactions-rejections-reasons'
  ];

  if (partnershipsStore.hasPartnerships) {
    collections.push('app.partnership');
    collections.push('webapp.partnership');
  }

  await refdataStore.loadCollections(collections);
  state.isRefdataLoaded = true;
}

watch(
  () => communication.value.locale,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      if (i18n.getCurrentLocale() !== communication.value.locale) {
        state.isRefdataLoaded = false;
        i18n.setLocale(communication.value.locale);
        await loadRequiredRefdata();
        state.isRefdataLoaded = true;
      }
    }
  }
);

async function loadUserData() {
  const userData: AxiosResponse = await apiGw.get(
    createResourceUri(NaturalPersonURI.Me, { queryParams: { contactDetails: true } })
  );

  if (userData) {
    userStore.setUser(userData.data);
    const profileData: AxiosResponse = await apiGw.get(
      createResourceUri(NaturalPersonURI.BaseV2, { uid: userStore.uid, resource: NaturalPersonURI.Profile })
    );

    profileStore.setProfile(profileData.data);
    contextSwitchStore.setUserContext(contextSwitchStore.getPrivateContext);
    await partnershipsStore.loadPartnerships(
      createResourceUri(NaturalPersonURI.BaseV2, {
        uid: userStore.uid,
        resource: NaturalPersonURI.PartnershipsEngagements,
        queryParams: {
          noConstitution: true,
          partnershipStatus: [
            PartnershipStatus.New,
            PartnershipStatus.Registered,
            PartnershipStatus.Screened,
            PartnershipStatus.Identified,
            PartnershipStatus.Bankable,
            PartnershipStatus.Passive,
            PartnershipStatus.Cancelling
          ]
        }
      })
    );
    exportsStore.loadDataSharingExports();
  }
}
</script>
